import { useEffect, useRef } from 'react';

/**
 * useQuestionInteractiveReset
 * Fokus: kumpulkan efek-efek reset/cleanup terkait interactive mode supaya QuestionsForm.jsx lebih readable.
 * - Cleanup interactive_type yang tidak valid saat pindah `type`
 * - Reset choice/answer berdasarkan support interactive + interactive_type
 * - Handle transisi dragpin -> clickable supaya nilai dragpin tidak nyangkut
 */
export function useQuestionInteractiveReset(params) {
  const {
    type,
    isInteractive,
    interactiveType,
    clickableType,
    choice,
    answer,
    dispatch,
    setField,
    resetChoiceAndAnswer,
    CLICKABLE_LENGTH_BY_TYPE,
  } = params;

  // Hook ini memang cuma relevan untuk 2 tipe yang punya interactive mode di form ini
  const isInteractiveSupportedType =
    type === 'sentence_arrangement' || type === 'fill_in_the_blanks';

  // Track perubahan interactive_type untuk handle transisi mode (dragpin -> clickable)
  const prevInteractiveTypeRef = useRef(interactiveType);

  // Track transisi is_interactive supaya reset hanya terjadi saat toggle, bukan tiap ketik
  const prevYesInteractiveRef = useRef(isInteractive === true || isInteractive === 1);

  useEffect(() => {
    prevInteractiveTypeRef.current = interactiveType;
  }, [interactiveType]);

  useEffect(() => {
    // ✅ Kalau bukan tipe yang disupport, jangan lakukan apa-apa.
    // Ini penting supaya multiple_choice tidak ikut ke-reset ketika user mengetik di RichEditor.
    if (!isInteractiveSupportedType) return;

    // ✅ Cleanup: kalau pindah type, buang interactive_type yang tidak valid
    // Tujuan: biar tidak "nyangkut" dari fill_in_the_blanks (clickable/dragpin) ke sentence_arrangement
    const isSentenceArrangement = type === 'sentence_arrangement';
    const isFillInTheBlanks = type === 'fill_in_the_blanks';

    const isInvalidForSentenceArrangement =
      isSentenceArrangement && (interactiveType === 'clickable' || interactiveType === 'dragpin');

    const isInvalidForFillInTheBlanks =
      isFillInTheBlanks && (
        interactiveType === 'pie_chart' ||
        interactiveType === 'bar_chart' ||
        interactiveType === 'line_chart' ||
        interactiveType === 'sequential'
      );

    if (isInvalidForSentenceArrangement) {
      // Clear fields yang hanya dipakai oleh fill_in_the_blanks interactive
      dispatch(setField({ field: 'interactive_type', value: '' }));
      dispatch(setField({ field: 'clickable_type', value: '' }));
      dispatch(setField({ field: 'room_count', value: '' }));
      dispatch(setField({ field: 'pin_count', value: '' }));
      // ✅ Clear detail dragpin agar tidak nyangkut
      dispatch(setField({ field: 'interactive_detail', value: {} }));

      // Reset agar input kembali 1 (tidak ikut banyak seperti clickable)
      resetChoiceAndAnswer(1);
      return;
    }

    if (isInvalidForFillInTheBlanks) {
      dispatch(setField({ field: 'interactive_type', value: '' }));
      resetChoiceAndAnswer(1);
    }
  }, [type, interactiveType, dispatch, setField, resetChoiceAndAnswer, isInteractiveSupportedType]);

  useEffect(() => {
    if (!isInteractiveSupportedType) return;

    const yesInteractive = isInteractive === true || isInteractive === 1;

    // ✅ IMPORTANT: untuk mode non-interactive, jangan lakukan reset berulang saat user mengetik.
    // Reset hanya sekali ketika user baru saja mematikan interactive (true -> false).
    const prevYesInteractive = prevYesInteractiveRef.current;
    prevYesInteractiveRef.current = yesInteractive;

    if (!yesInteractive) {
      if (prevYesInteractive) {
        // Clear field yang khusus interactive agar tidak nyangkut ketika toggle off
        dispatch(setField({ field: 'interactive_type', value: '' }));
        dispatch(setField({ field: 'clickable_type', value: '' }));
        dispatch(setField({ field: 'room_count', value: '' }));
        dispatch(setField({ field: 'pin_count', value: '' }));
        // ✅ Clear detail dragpin agar state bersih
        dispatch(setField({ field: 'interactive_detail', value: {} }));

        // Reset sekali agar kembali ke default 1 item (sesuai behavior sebelumnya saat keluar dari interactive)
        resetChoiceAndAnswer(1);
      }
      return;
    }

    // ✅ Special case: sequential (sentence_arrangement) harus bisa diketik (answer berubah terus).
    // Jangan reset choice/answer di mode ini.
    const isSequentialInteractive =
      type === 'sentence_arrangement' && interactiveType === 'sequential';
    if (isSequentialInteractive) return;

    // ✅ clickable/dragpin HANYA valid untuk fill_in_the_blanks
    const isClickableInteractive =
      type === 'fill_in_the_blanks' && interactiveType === 'clickable';
    const isDragpinInteractive =
      type === 'fill_in_the_blanks' && interactiveType === 'dragpin';

    // ✅ Jika interactive + clickable, sesuaikan dengan jumlah tipe
    if (isClickableInteractive) {
      const length = CLICKABLE_LENGTH_BY_TYPE[clickableType];
      if (!length) return; // Belum pilih tipe clickable

      const choiceLen = Array.isArray(choice) ? choice.length : 0;
      const answerLen = Array.isArray(answer) ? answer.length : 0;

      if (choiceLen !== length || answerLen !== length) {
        // ✅ Preserve nilai existing saat edit (jangan wipe semua jawaban)
        // Clickable pada fill_in_the_blanks tidak butuh choice dari backend, tapi state choice tetap dipakai untuk panjang array.
        const safeChoice = Array.isArray(choice) ? choice : [];
        const safeAnswer = Array.isArray(answer) ? answer : [];

        const nextChoice = Array.from({ length }, (_, idx) => safeChoice[idx] ?? '');
        const nextAnswer = Array.from({ length }, (_, idx) => safeAnswer[idx] ?? '');

        dispatch(setField({ field: 'choice', value: nextChoice }));
        dispatch(setField({ field: 'answer', value: nextAnswer }));
      }
      return;
    }

    // ✅ Jika interactive + dragpin, JANGAN reset answer ke [''] karena answer akan digenerate otomatis
    if (isDragpinInteractive) return;

    // ✅ Jika interactive tapi BUKAN clickable/sequential/dragpin:
    // Jangan auto-reset choice/answer di setiap render/ketik.
    // Bug sebelumnya: untuk sentence_arrangement tipe pie_chart/bar_chart/line_chart,
    // user tidak bisa input Label/Nilai karena tiap onChange langsung di-reset ke [''].
    // Yang kita butuhkan cuma inisialisasi sekali ketika array masih kosong/invalid.
    const choiceLen = Array.isArray(choice) ? choice.length : 0;
    const answerLen = Array.isArray(answer) ? answer.length : 0;

    // Keep existing behavior untuk state yang sudah valid (termasuk edit/update).
    // Hanya init jika keduanya benar-benar kosong.
    if (choiceLen === 0 && answerLen === 0) {
      resetChoiceAndAnswer(1);
      return;
    }

    // Jika salah satunya kosong (data corrupt / partial), normalisasi minimal.
    // Ini menjaga input tetap editable tanpa menghapus data yang sudah ada.
    if (choiceLen === 0 && answerLen > 0) {
      dispatch(setField({ field: 'choice', value: Array.from({ length: answerLen }, () => '') }));
      return;
    }

    if (answerLen === 0 && choiceLen > 0) {
      dispatch(setField({ field: 'answer', value: Array.from({ length: choiceLen }, () => '') }));
    }
  }, [
    type,
    isInteractive,
    interactiveType,
    clickableType,
    choice,
    answer,
    dispatch,
    setField,
    resetChoiceAndAnswer,
    CLICKABLE_LENGTH_BY_TYPE,
    isInteractiveSupportedType,
  ]);

  useEffect(() => {
    if (!isInteractiveSupportedType) return;

    const prev = prevInteractiveTypeRef.current;
    const next = interactiveType;

    // Fokus: hanya saat fill_in_the_blanks + interactive aktif
    const isFillIn = type === 'fill_in_the_blanks';
    const yesInteractive = isInteractive === 1;

    if (!isFillIn || !yesInteractive) return;

    // ✅ Kalau pindah dari dragpin -> clickable, bersihkan nilai dragpin yang nyangkut di answer/choice
    if (prev === 'dragpin' && next === 'clickable') {
      // Clear field yang hanya relevan untuk dragpin (biar state form konsisten)
      dispatch(setField({ field: 'room_count', value: '' }));
      dispatch(setField({ field: 'pin_count', value: '' }));
      // ✅ Clear detail dragpin agar tidak terbawa
      dispatch(setField({ field: 'interactive_detail', value: {} }));

      // Reset sesuai panjang clickable_type (kalau belum dipilih, fallback 1 dulu)
      const clickableLen = CLICKABLE_LENGTH_BY_TYPE[clickableType] || 1;
      resetChoiceAndAnswer(clickableLen);
    }
  }, [
    type,
    isInteractive,
    interactiveType,
    clickableType,
    dispatch,
    setField,
    resetChoiceAndAnswer,
    CLICKABLE_LENGTH_BY_TYPE,
    isInteractiveSupportedType,
  ]);
}